import React, { useCallback, useState } from 'react';
import classnames from 'classnames';
import { Swipeable, SwipeCallback } from 'react-swipeable';
import { PapiDeviation } from '@wix/da-papi-types';
import { DuperbrowseNavigationItem } from '../../../Duperbrowse/redux/types';
import s from './Duperswiper.scss';

export interface Props {
  isInZoomModal?: boolean;
  isEnabled: boolean;
  width?: number | 'auto';
  height?: number | 'auto';
  deviation: PapiDeviation;
  navigation: DuperbrowseNavigationItem;
  preloadEnabled?: boolean;
  className?: string;

  moveNext: () => void;
  movePrev: () => void;
  children: (deviation: PapiDeviation) => React.ReactNode;
}

const Duperswiper: React.FC<Props> = ({
  children,
  deviation,
  width,
  height,
  navigation: { nextItem, prevItem },
  isEnabled,
  className,
  moveNext,
  movePrev,
}) => {
  const [xOffset, setOffset] = useState<number | string>(0);

  const onSwipingLeft = useCallback(
    (absX: number) => {
      if (!nextItem) {
        if (xOffset) {
          setOffset(0);
        }
        return;
      }
      setOffset(-absX);
    },
    [nextItem, xOffset]
  );

  const onSwipingRight = useCallback(
    (absX: number) => {
      // do nothing if there's nothing in the current direction
      if (!prevItem) {
        if (xOffset) {
          setOffset(0);
        }
        return;
      }
      setOffset(absX);
    },
    [prevItem, xOffset]
  );

  const shouldIgnoreSwipeTarget = (target: EventTarget | null) =>
    !!target && (target as HTMLElement).closest('.additional-slider, .v-vlite');

  const onSwiping: SwipeCallback = useCallback(
    ({ dir, absX, event }) => {
      if (shouldIgnoreSwipeTarget(event.target)) {
        return;
      }

      switch (dir) {
        case 'Left':
          onSwipingLeft(absX);
          return;
        case 'Right':
          onSwipingRight(absX);
          return;
        default:
      }
    },
    [onSwipingLeft, onSwipingRight]
  );

  const onSwipedLeft = useCallback(
    sw => {
      if (shouldIgnoreSwipeTarget(sw.event.target)) {
        return;
      }

      if (!nextItem || sw.deltaX < 40) {
        setOffset(0);
        return;
      }

      setOffset(width ? -width : '-100%');
      moveNext();
    },
    [moveNext, nextItem, width]
  );

  const onSwipedRight = useCallback(
    sw => {
      if (shouldIgnoreSwipeTarget(sw.event.target)) {
        return;
      }

      if (!prevItem || sw.deltaX > -40) {
        setOffset(0);
        return;
      }

      setOffset(width || '100%');
      movePrev();
    },
    [movePrev, prevItem, width]
  );

  if (!isEnabled) {
    return <>{children(deviation)}</>;
  }

  const transformStyle = { transform: `translateX(${xOffset}px)` };

  return (
    <Swipeable
      trackMouse
      onSwiping={onSwiping}
      onSwipedLeft={onSwipedLeft}
      onSwipedRight={onSwipedRight}
      className={s['swipeable']}
    >
      <div
        className={classnames(s['root'], className)}
        style={{ width, height }}
      >
        <div style={transformStyle} className={classnames(s['transform'])}>
          {children(deviation)}
        </div>
        {prevItem && Number(xOffset) > 0 && (
          <div
            style={transformStyle}
            className={classnames(s['preview'], s['prev'])}
          >
            {children(prevItem)}
          </div>
        )}
        {nextItem && Number(xOffset) < 0 && (
          <div
            style={transformStyle}
            className={classnames(s['preview'], s['next'])}
          >
            {children(nextItem)}
          </div>
        )}
      </div>
    </Swipeable>
  );
};
Duperswiper.displayName = 'Duperswiper';

export default Duperswiper;
